/**
 * Types for document context selection in Case Discovery
 * Used to filter which documents are used for AI context
 */

import {
  MOVABLE_DOCUMENT_CATEGORIES,
  type MovableDocumentCategory,
} from '@hector/shared-types';
import {
  DOCUMENT_CATEGORIES as CENTRAL_DOCUMENT_CATEGORIES,
  DOCUMENT_SUBCATEGORIES as CENTRAL_DOCUMENT_SUBCATEGORIES,
} from '@/data/documentCategories';

/**
 * Main document categories.
 *
 * Adds the synthetic `'all'` filter on top of the canonical category keys —
 * the AI context selector is the only surface that needs this aggregate.
 * Includes correspondence filed from Outlook independently of connector setup.
 */
export type DocumentCategory = 'all' | MovableDocumentCategory;

/**
 * Subcategories for partie_adverse
 */
export type DocumentSubcategory =
  | 'pieces_communiquees'    // Communicated documents
  | 'actes_communiques';     // Communicated legal acts

/**
 * Single document context selection
 * Represents one category/subcategory pair
 */
export interface DocumentContextSelection {
  /** Selected document category */
  document_category: DocumentCategory;

  /**
   * Subcategory (only for partie_adverse)
   * null for all other categories
   */
  document_subcategory: DocumentSubcategory | null;
}

/**
 * Document context filter (legacy single selection)
 * Used to specify which documents should be included in AI context
 * @deprecated Use DocumentContextMultiFilter for multiselect
 */
export interface DocumentContextFilter {
  /** Selected document category */
  document_category: DocumentCategory;

  /**
   * Subcategory (only for partie_adverse)
   * null for all other categories
   */
  document_subcategory: DocumentSubcategory | null;
}

/**
 * Document context multi-filter
 * Used to specify multiple document categories for AI context
 */
export interface DocumentContextMultiFilter {
  /** Array of selected categories with their subcategories */
  selections: DocumentContextSelection[];
}

/**
 * Category configuration for UI display
 */
export interface CategoryConfig {
  value: DocumentCategory;
  label: string;
  icon?: string;
  description?: string;
  hasSubcategories?: boolean;
}

/**
 * Subcategory configuration for UI display
 */
export interface SubcategoryConfig {
  value: DocumentSubcategory;
  label: string;
  parentCategory: DocumentCategory;
}

/**
 * Default document categories for display.
 *
 * Built from the shared movable-destinations contract: any new valid document
 * destination automatically lands in the AI context selector. Descriptions
 * stay local because they are tuned for the AI-context-selector UX, not for
 * the Timeline badges.
 */

// AI-context-specific descriptions. Falls back to the canonical description
// if a new category lands without a tuned blurb.
const AI_CONTEXT_DESCRIPTIONS: Partial<Record<MovableDocumentCategory, string>> = {
  nos_pieces: 'Pièces client',
  notes_projets: 'Notes internes',
  procedure: 'Actes et procédure',
  partie_adverse: 'Documents adverses',
  recherche: 'Jurisprudence',
};

const ALL_DOCUMENTS_OPTION: CategoryConfig = {
  value: 'all',
  label: 'Tous les documents',
  description: 'Tous les documents',
  hasSubcategories: false,
};

export const DOCUMENT_CATEGORIES: CategoryConfig[] = [
  ALL_DOCUMENTS_OPTION,
  ...MOVABLE_DOCUMENT_CATEGORIES
    .map((key): CategoryConfig => {
      const central = CENTRAL_DOCUMENT_CATEGORIES[key];
      return {
        value: key,
        label: central.label,
        description: AI_CONTEXT_DESCRIPTIONS[key] ?? central.description,
        hasSubcategories: central.hasSubcategories,
      };
    }),
];

/**
 * Subcategories for partie_adverse, derived from the canonical module.
 */
export const PARTIE_ADVERSE_SUBCATEGORIES: SubcategoryConfig[] = [
  {
    value: 'pieces_communiquees',
    label: CENTRAL_DOCUMENT_SUBCATEGORIES.pieces_communiquees.label,
    parentCategory: 'partie_adverse',
  },
  {
    value: 'actes_communiques',
    label: CENTRAL_DOCUMENT_SUBCATEGORIES.actes_communiques.label,
    parentCategory: 'partie_adverse',
  },
];

/**
 * Default context filter (all documents)
 */
export const DEFAULT_DOCUMENT_CONTEXT: DocumentContextFilter = {
  document_category: 'all',
  document_subcategory: null
};

/**
 * Default multi-context filter (all documents selected)
 */
export const DEFAULT_DOCUMENT_MULTI_CONTEXT: DocumentContextMultiFilter = {
  selections: [{ document_category: 'all', document_subcategory: null }]
};

/**
 * Helper to check if a selection is in the multi-filter
 */
export function isSelectionInMultiFilter(
  multiFilter: DocumentContextMultiFilter,
  category: DocumentCategory,
  subcategory: DocumentSubcategory | null = null
): boolean {
  return multiFilter.selections.some(
    sel => sel.document_category === category && sel.document_subcategory === subcategory
  );
}

/**
 * Helper to get display label for multi-filter
 */
export function getMultiFilterDisplayLabel(multiFilter: DocumentContextMultiFilter): string {
  const { selections } = multiFilter;

  if (selections.length === 0) {
    return 'Aucun contexte';
  }

  // If "all" is selected, show that
  if (selections.some(s => s.document_category === 'all')) {
    return 'Tous les documents';
  }

  // If only one selection
  if (selections.length === 1) {
    const sel = selections[0];
    if (!sel) return 'Contexte';
    const category = DOCUMENT_CATEGORIES.find(c => c.value === sel.document_category);

    if (sel.document_subcategory && sel.document_category === 'partie_adverse') {
      const subcategory = PARTIE_ADVERSE_SUBCATEGORIES.find(
        s => s.value === sel.document_subcategory
      );
      return `${category?.label} → ${subcategory?.label}`;
    }

    return category?.label || 'Contexte';
  }

  // Multiple selections
  return `${selections.length} contextes`;
}

/**
 * Convert multi-filter to array of categories for backend
 */
export function multiFilterToCategories(multiFilter: DocumentContextMultiFilter): {
  document_categories: DocumentCategory[];
  document_subcategories: { category: DocumentCategory; subcategory: DocumentSubcategory }[];
} {
  const categories: DocumentCategory[] = [];
  const subcategories: { category: DocumentCategory; subcategory: DocumentSubcategory }[] = [];

  for (const sel of multiFilter.selections) {
    if (!categories.includes(sel.document_category)) {
      categories.push(sel.document_category);
    }
    if (sel.document_subcategory) {
      subcategories.push({
        category: sel.document_category,
        subcategory: sel.document_subcategory
      });
    }
  }

  return { document_categories: categories, document_subcategories: subcategories };
}
